import { useLazyQuery } from '@apollo/client'
import { usePlanContext } from 'features/RoutingProjects/containers/PlanContainer'
import { GET_ROUTING_PROJECT_PLAN_EXPORT } from 'features/RoutingProjects/queries'
import { RoutingProjectPlanExportStatus } from 'gql/generated/graphql'
import { min } from 'lodash'
import { ReactElement, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { downloadFileFromUrl } from 'utils/file'

const MAX_EXPORT_PROGRESS = 10 // Maximum progress value in seconds
const MAX_FILL_PERCENT = 95 // Maximum fill percent for the donut chart
const CIRCLE_SIZE = 46 // Size of the SVG circle
const CIRCLE_STROKE_WIDTH = 4 // Stroke width of the SVG circle
const CIRCLE_RADIUS = (CIRCLE_SIZE - CIRCLE_STROKE_WIDTH) / 2 // Radius of the SVG circle
const CIRCLE_CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS // Circumference of the SVG circle
const POLLING_INTERVAL = 1000 // 1 second
const EXPORT_COMPLETE_DELAY = 1000 // 1 second

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
`

const CircleSvg = styled.svg<{ shouldRotate?: boolean }>`
  ${({ shouldRotate }): string | undefined => (shouldRotate ? `transform: rotate(-90deg);` : '')}
`

const ProgressCircle = styled.circle<{ dashoffset: number }>`
  transform: scale(1, -1);
  transform-origin: center;
  transition: stroke-dashoffset 1s linear;
  stroke-dashoffset: ${({ dashoffset }): number => dashoffset}px;
`

const IconSvg = styled.svg`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

interface IExportPlanStatusProps {
  planId?: string
  planName?: string
  routingProjectId?: string
}

export const ExportingPlanStatus = ({ planId, planName, routingProjectId }: IExportPlanStatusProps): ReactElement => {
  const { exportProgress, setExportProgress, setIsExportingPlan, hasStartedPlanExport } = usePlanContext()
  const [getPlanExport] = useLazyQuery(GET_ROUTING_PROJECT_PLAN_EXPORT, {
    fetchPolicy: 'network-only'
  })

  const exportCompleteTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const initialFetchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleSuccessfulExport = async (url: string): Promise<void> => {
    setExportProgress(MAX_EXPORT_PROGRESS)

    try {
      const filename = `plan-${planName}-export.csv`
      await downloadFileFromUrl(url, filename)
    } catch (error) {
      console.error('Failed to download exported file:', error)
    }

    if (exportCompleteTimeoutRef.current) {
      clearTimeout(exportCompleteTimeoutRef.current)
    }
    exportCompleteTimeoutRef.current = setTimeout(() => {
      setIsExportingPlan(false)
    }, EXPORT_COMPLETE_DELAY)
  }

  const pollForExportCompletion = (): void => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current)
    }
    pollingTimeoutRef.current = setTimeout(() => {
      fetchPlanExport()
    }, POLLING_INTERVAL)
  }

  const fetchPlanExport = async (): Promise<void> => {
    if (!planId || !routingProjectId) {
      console.error('Missing required parameters: planId or routingProjectId')
      return
    }

    try {
      const response = await getPlanExport({
        variables: {
          input: {
            id: routingProjectId,
            planID: planId
          }
        }
      })

      const exportData = response.data?.routingProjectPlanExport
      if (!exportData) {
        console.error('No export data received')
        return
      }

      const { status, url } = exportData

      if (status === RoutingProjectPlanExportStatus.Exported && url) {
        await handleSuccessfulExport(url)
      } else {
        pollForExportCompletion()
      }
    } catch (error) {
      console.error('Error fetching plan export:', error)
      setIsExportingPlan(false)
    }
  }

  useEffect(() => {
    setExportProgress(1)
    const timer = setInterval(() => {
      setExportProgress((prev) => {
        if (prev >= MAX_EXPORT_PROGRESS) {
          clearInterval(timer)
          return MAX_EXPORT_PROGRESS
        }
        return prev + 1
      })
    }, 1000)
    return () => {
      setExportProgress(0)
      clearInterval(timer)
    }
  }, [])

  useEffect(() => {
    if (hasStartedPlanExport) {
      if (initialFetchTimeoutRef.current) {
        clearTimeout(initialFetchTimeoutRef.current)
      }
      initialFetchTimeoutRef.current = setTimeout(() => {
        fetchPlanExport()
      }, 1000)
      return () => {
        if (initialFetchTimeoutRef.current) {
          clearTimeout(initialFetchTimeoutRef.current)
        }
      }
    }
  }, [hasStartedPlanExport])

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (exportCompleteTimeoutRef.current) {
        clearTimeout(exportCompleteTimeoutRef.current)
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current)
      }
      if (initialFetchTimeoutRef.current) {
        clearTimeout(initialFetchTimeoutRef.current)
      }
    }
  }, [])

  // SVG donut chart
  const fillPercent = min([MAX_FILL_PERCENT, (exportProgress / MAX_EXPORT_PROGRESS) * 100]) as number
  const dashoffset = ((100 - fillPercent) / 100) * CIRCLE_CIRCUMFERENCE

  return (
    <Container>
      <CircleSvg width={CIRCLE_SIZE} height={CIRCLE_SIZE} viewBox={`0 0 ${CIRCLE_SIZE} ${CIRCLE_SIZE}`} shouldRotate>
        {/* Background circle */}
        <circle
          cx={CIRCLE_SIZE / 2}
          cy={CIRCLE_SIZE / 2}
          r={CIRCLE_RADIUS}
          fill="transparent"
          stroke="#EAEAFF"
          strokeWidth={CIRCLE_STROKE_WIDTH}
        />
        {/* Progress circle */}
        <ProgressCircle
          cx={CIRCLE_SIZE / 2}
          cy={CIRCLE_SIZE / 2}
          r={CIRCLE_RADIUS}
          fill="transparent"
          stroke="#5D59D7"
          strokeWidth={CIRCLE_STROKE_WIDTH}
          strokeDasharray={CIRCLE_CIRCUMFERENCE}
          dashoffset={dashoffset}
          strokeLinecap="round"
        />
      </CircleSvg>
      <IconSvg x="13.5" y="12.5" width="19" height="21" viewBox="20 19 16 17">
        <path
          d="M26.2502 29.9914L23.1404 26.8816C22.8172 26.5584 22.4149 26.3969 21.9335 26.3969C21.4521 26.3969 21.0497 26.5584 20.7266 26.8816C20.4034 27.2044 20.2418 27.6116 20.2418 28.1031C20.2418 28.5951 20.4034 28.9999 20.7266 29.3176L26.5465 35.1375C26.9681 35.5595 27.46 35.7704 28.0223 35.7704C28.5851 35.7704 29.0772 35.5595 29.4987 35.1375L35.3187 29.3176C35.6418 28.9999 35.8034 28.5951 35.8034 28.1031C35.8034 27.6116 35.6418 27.2044 35.3187 26.8816C34.9955 26.5584 34.5881 26.3969 34.0966 26.3969C33.605 26.3969 33.2002 26.5584 32.8821 26.8816L29.7723 29.9914V21.1107C29.7723 20.6149 29.6047 20.1993 29.2695 19.8641C28.9339 19.5285 28.518 19.3607 28.0217 19.3607C27.5259 19.3607 27.1067 19.5285 26.7641 19.8641C26.4215 20.1993 26.2502 20.6149 26.2502 21.1107V29.9914Z"
          fill="#EAEAFF"
        />
      </IconSvg>
    </Container>
  )
}

export const PlanExportedStatus = (): ReactElement | null => {
  const { setHasStartedPlanExport } = usePlanContext()
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setHasStartedPlanExport(false)
    }, 300)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return (
    <Container>
      <CircleSvg width={CIRCLE_SIZE} height={CIRCLE_SIZE} viewBox={`0 0 ${CIRCLE_SIZE} ${CIRCLE_SIZE}`}>
        <circle
          cx={CIRCLE_SIZE / 2}
          cy={CIRCLE_SIZE / 2}
          r={CIRCLE_RADIUS}
          fill="transparent"
          stroke="#5D59D7"
          strokeWidth={CIRCLE_STROKE_WIDTH}
        />
      </CircleSvg>
      <IconSvg x="13.5" y="12.5" width="19" height="21" viewBox="20 19 16 17">
        <path
          d="M26.2502 29.9914L23.1404 26.8816C22.8172 26.5584 22.4149 26.3969 21.9335 26.3969C21.4521 26.3969 21.0497 26.5584 20.7266 26.8816C20.4034 27.2044 20.2418 27.6116 20.2418 28.1031C20.2418 28.5951 20.4034 28.9999 20.7266 29.3176L26.5465 35.1375C26.9681 35.5595 27.46 35.7704 28.0223 35.7704C28.5851 35.7704 29.0772 35.5595 29.4987 35.1375L35.3187 29.3176C35.6418 28.9999 35.8034 28.5951 35.8034 28.1031C35.8034 27.6116 35.6418 27.2044 35.3187 26.8816C34.9955 26.5584 34.5881 26.3969 34.0966 26.3969C33.605 26.3969 33.2002 26.5584 32.8821 26.8816L29.7723 29.9914V21.1107C29.7723 20.6149 29.6047 20.1993 29.2695 19.8641C28.9339 19.5285 28.518 19.3607 28.0217 19.3607C27.5259 19.3607 27.1067 19.5285 26.7641 19.8641C26.4215 20.1993 26.2502 20.6149 26.2502 21.1107V29.9914Z"
          fill="#5D59D7"
        />
      </IconSvg>
    </Container>
  )
}
